import {
  parseAsBoolean,
  parseAsString,
  useQueryState,
  useQueryStates,
  parseAsFloat,
  parseAsStringEnum,
  createParser,
  createLoader,
  createSerializer,
} from "nuqs";
import * as SCHEMA from "../data/schema";
import { z } from "zod";

// From the docs
export function createZodCodecParser<
  Input extends z.ZodType,
  Output extends z.ZodType
>(
  codec: z.ZodCodec<Input, Output>,
  eq: (a: z.output<Output>, b: z.output<Output>) => boolean = (a, b) => a === b
) {
  return createParser<z.output<Output>>({
    parse(query) {
      // console.time("zodec parse");
      const result = codec.parse(JSON.parse(query));
      // console.timeEnd("zodec parse");
      return result;
    },
    serialize(value) {
      // console.time("zodec serialize");
      const result = JSON.stringify(codec.encode(value));
      // console.timeEnd("zodec serialize");
      return result;
    },
    eq,
  });
}

// Single source of truth - keys match URL params
export const defaultFilter = {
  isFromSharedLink: false,
  isShaken: false,
  title: "",
  author: "",
  spices: new Map() as Map<number, SCHEMA.Spice>,
  unit: SCHEMA.Units.GRAM,
  quantity: 1,
};

// Parsers reference the same defaults
const filterParsers = {
  isFromSharedLink: parseAsBoolean.withDefault(defaultFilter.isFromSharedLink),
  isShaken: parseAsBoolean.withDefault(defaultFilter.isShaken),
  title: parseAsString.withDefault(defaultFilter.title),
  author: parseAsString.withDefault(defaultFilter.author),
  spices: createZodCodecParser(SCHEMA.codec).withDefault(defaultFilter.spices),
  unit: parseAsStringEnum(Object.values(SCHEMA.Units)).withDefault(defaultFilter.unit), //prettier-ignore
  quantity: parseAsFloat.withDefault(defaultFilter.quantity),
};

// Individual hooks — subscribe to only the state you need
export const useFilterIsFromSharedLink = () => useQueryState('isFromSharedLink', filterParsers.isFromSharedLink);
export const useFilterIsShaken = () => useQueryState('isShaken', filterParsers.isShaken);
export const useFilterTitle = () => useQueryState('title', filterParsers.title);
export const useFilterAuthor = () => useQueryState('author', filterParsers.author);
export const useFilterSpices = () => useQueryState('spices', filterParsers.spices);
export const useFilterUnit = () => useQueryState('unit', filterParsers.unit);
export const useFilterQuantity = () => useQueryState('quantity', filterParsers.quantity);

// Batched setter without subscribing to all values
export function useSetFilter() {
  const [, setFilter] = useQueryStates(filterParsers);
  const resetFilter = () => setFilter({ ...defaultFilter });
  return { setFilter, resetFilter };
}

// Convenience hook for components that need everything
export function useFilter() {
  const [isFromSharedLink, setIsFromSharedLink] = useFilterIsFromSharedLink();
  const [isShaken, setIsShaken] = useFilterIsShaken();
  const [title, setTitle] = useFilterTitle();
  const [author, setAuthor] = useFilterAuthor();
  const [spices, setSpices] = useFilterSpices();
  const [unit, setUnit] = useFilterUnit();
  const [quantity, setQuantity] = useFilterQuantity();
  const { setFilter, resetFilter } = useSetFilter();

  return {
    isFromSharedLink,
    isShaken,
    title,
    author,
    spices,
    unit,
    quantity,
    setFilter,
    setIsShaken,
    setIsFromSharedLink,
    setSpices,
    setUnit,
    setTitle,
    setAuthor,
    setQuantity,
    resetFilter,
  };
}

const loadFilter = createLoader(filterParsers);
const serializeFilter = createSerializer(filterParsers);

export const getFilterSnapshot = () => loadFilter(window.location.search);
export const generateShareableUrl = () => {
  const snapshot = getFilterSnapshot();
  return serializeFilter(window.location.origin + window.location.pathname, {
    ...snapshot,
    isFromSharedLink: true,
  });
};

export const resetFilter = () => {
  const { pathname, hash } = window.location;
  const url = serializeFilter(pathname, { ...defaultFilter }) + hash; // append existing fragment
  window.history.replaceState({}, "", url);
  window.history.replaceState({}, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

export const isDefaultFilter = (filter: typeof defaultFilter) => {
  return (
    serializeFilter("", filter) ===
    serializeFilter("", defaultFilter)
  );
};
